import { forwardRef, type FormEvent } from "react";
import { motion } from "framer-motion";
import { Composer } from "../Composer";
import { FollowupChips } from "../FollowupChips";
import { DURATION, EASE } from "../motion";
import type { Followup } from "../types";

interface DockedComposerBarProps {
  barShown: boolean;
  streaming: boolean;
  followups: (Followup | string)[];
  draft: string;
  clientName: string;
  onDraftChange: (next: string) => void;
  onSubmit: (e: FormEvent) => void;
  onStop: () => void;
  onPickFollowup: (q: string) => void;
  onFocusReveal: () => void;
}

export const DockedComposerBar = forwardRef<HTMLInputElement, DockedComposerBarProps>(
  function DockedComposerBar(
    {
      barShown,
      streaming,
      followups,
      draft,
      clientName,
      onDraftChange,
      onSubmit,
      onStop,
      onPickFollowup,
      onFocusReveal,
    },
    inputRef,
  ) {
    return (
      <motion.div
        className="sticky bottom-0 left-0 right-0 border-t border-border/40 bg-background/95"
        initial={false}
        animate={barShown ? { y: 0, opacity: 1 } : { y: "110%", opacity: 0 }}
        transition={
          barShown
            ? { duration: DURATION.medium, ease: EASE.out }
            : { duration: DURATION.short, ease: EASE.in }
        }
        style={{ pointerEvents: barShown ? "auto" : "none" }}
      >
        {!streaming && followups.length > 0 ? (
          // Follow-up chips sit ABOVE the composer (after the first answer) and
          // ride the bar's scroll-reveal so they never obfuscate the answer
          // mid-read.
          <div className="mx-auto max-w-6xl px-6 pt-3">
            <FollowupChips items={followups} onPick={onPickFollowup} variant="inline" />
          </div>
        ) : null}
        <div className="mx-auto max-w-6xl px-6 pb-4 pt-2">
          <Composer
            ref={inputRef}
            placement="docked"
            draft={draft}
            onDraftChange={onDraftChange}
            onSubmit={onSubmit}
            streaming={streaming}
            onStop={onStop}
            clientName={clientName}
            onFocus={onFocusReveal}
          />
        </div>
      </motion.div>
    );
  },
);
